import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { URLS } from '../../constants';
import { API } from '../../utils/API';
import { getMenuId } from '../../utils/helpers';

const POLL_INTERVAL_MS = 2000;
const CURSOR_LIMIT = 100;

const hasKeyPrefix = (queryKey, prefix) =>
  Array.isArray(queryKey) && queryKey.length > 0 && queryKey[0] === prefix;

const invalidateProductQueries = async queryClient => {
  await queryClient.invalidateQueries({
    predicate: query =>
      hasKeyPrefix(query.queryKey, 'products') ||
      hasKeyPrefix(query.queryKey, 'search-products') ||
      hasKeyPrefix(query.queryKey, 'product'),
  });
};

const invalidateTierQueries = async queryClient => {
  await queryClient.invalidateQueries({
    predicate: query =>
      hasKeyPrefix(query.queryKey, 'pricing-tiers') ||
      hasKeyPrefix(query.queryKey, 'pricing-tier-products'),
  });
};

const getEventCursorKey = menuId => `menu_events_cursor_${menuId}`;

const MenuEventsSubscriber = () => {
  const queryClient = useQueryClient();
  const inFlightRef = useRef(false);

  useEffect(() => {
    const menuId = getMenuId();
    if (!menuId) return undefined;

    const cursorStorageKey = getEventCursorKey(menuId);
    let cursor = Number(localStorage.getItem(cursorStorageKey) || 0);

    const poll = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      try {
        const response = await API.get(URLS.menuEventsOutbox(), {
          params: { cursor, limit: CURSOR_LIMIT, menu_id: menuId },
        });

        const data = response?.data || {};
        const events = Array.isArray(data.events) ? data.events : [];
        const nextCursor = Number(data.next_cursor ?? cursor);

        let hasProductEvent = false;
        let hasTierEvent = false;

        for (const event of events) {
          if (!event?.event_type) continue;

          if (String(event.event_type).startsWith('product.')) {
            hasProductEvent = true;
            continue;
          }

          if (String(event.event_type).startsWith('pricing_tier.')) {
            hasTierEvent = true;
          }
        }

        if (hasProductEvent) {
          await invalidateProductQueries(queryClient);
        }
        if (hasTierEvent || hasProductEvent) {
          await invalidateTierQueries(queryClient);
        }

        cursor = nextCursor;
        localStorage.setItem(cursorStorageKey, String(cursor));
      } catch (error) {
        // Keep polling loop resilient; next cycle will retry.
      } finally {
        inFlightRef.current = false;
      }
    };

    const interval = setInterval(poll, POLL_INTERVAL_MS);
    poll();

    return () => {
      clearInterval(interval);
    };
  }, [queryClient]);

  return null;
};

export default MenuEventsSubscriber;
